/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { DataOptions } from "vuetify";
import _ from "lodash";
import { FILTER_NAMES } from "@/static/filterNames";
import {
  addAttributePrototype,
  addAttributePrototypeMapper,
  addAttributePrototypeMappers,
  addAttributePrototypeMappersFromSuggestions,
  editAttributePrototype,
  editAttributePrototypeMapper,
  getAttributePrototypeMappers,
  getAttributePrototypes,
  getMapperSuggestions,
  lockAttributePrototype,
  removeAttributePrototype,
  removeAttributePrototypeMapper,
  unlockAttributePrototype,
} from "@/requests/administration/servicesAndAttributes/administrationAttributePrototypes";
import {
  AttributePrototype,
  AttributePrototypesResponse,
} from "@/models/api/administration/servicesAndAttributes/AttributePrototype";
import { AttributePrototypeAddEdit } from "@/models/api/administration/servicesAndAttributes/AttributePrototypeAddEdit";
import {
  AttributePrototypeMapper,
  AttributePrototypeMapperResponse,
  MapperSuggestion,
} from "@/models/api/administration/servicesAndAttributes/AttributePrototypeMapper";
import { SnackbarModel } from "@/models/components/popup/Snackbar";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";

export interface AdministrationAttributesStateInterface {
  filters: Array<CriterionItemRequest> | null;
  dataTableOptions: DataOptions;
  attributes: Array<AttributePrototype>;
  selectedAttributeId: null | number;
  countItems: number | null;
  isDialog: boolean;
  attributePrototypeAddEdit: AttributePrototypeAddEdit;

  isMapperDialog: boolean;
  mapperCount: number | null;
  mapperOptions: DataOptions;
  mapperFilters: Array<CriterionItemRequest> | null;
  mapperItems: Array<AttributePrototypeMapper>;
  mapperSelectedItems: Array<AttributePrototypeMapper>;
  mapperSuggestions: Array<MapperSuggestion>;
  mapperAddModel: MapperSuggestion;

  mapperAttributeSearchDataTableOptions: DataOptions;
  mapperAttributeSearchAttributes: Array<AttributePrototype>;
  mapperAttributeSearchAttributesCount: number | null;
  mapperAttributeSearchSelectedAttributeId: null | number;
  mapperAttributeSearchFilters: Array<CriterionItemRequest> | null;
}

export const AdministrationAttributesState: AdministrationAttributesStateInterface = {
  filters: null,
  dataTableOptions: new DefaultDataOptions(),
  attributes: [],
  selectedAttributeId: null,
  countItems: null,
  isDialog: false,
  attributePrototypeAddEdit: new AttributePrototypeAddEdit(),

  isMapperDialog: false,
  mapperCount: null,
  mapperItems: [],
  mapperOptions: new DefaultDataOptions(),
  mapperFilters: null,
  mapperSelectedItems: [],
  mapperSuggestions: [],
  mapperAddModel: new MapperSuggestion(),

  mapperAttributeSearchDataTableOptions: new DefaultDataOptions(),
  mapperAttributeSearchAttributes: [],
  mapperAttributeSearchAttributesCount: null,
  mapperAttributeSearchSelectedAttributeId: null,
  mapperAttributeSearchFilters: null,
};

const AdministrationAttributesModule = defineModule({
  state: AdministrationAttributesState as AdministrationAttributesStateInterface,
  namespaced: true as const,
  getters: {
    selectedAttributeName(...args): string {
      const { state } = _moduleGetterContext(args);
      return state.attributes.find((v) => v.id === state.selectedAttributeId)?.defaultLabel as string;
    },
  },
  mutations: {
    setFilters(state, param: Array<CriterionItemRequest> | null) {
      state.filters = param;
    },
    setDataTableOptions(state, param: DataOptions) {
      state.dataTableOptions = param;
    },
    setAttributes(state, param: AttributePrototypesResponse) {
      state.attributes = param.items;
      state.countItems = param.totalCount;
    },
    setSelectedAttributeId(state, param: number | null) {
      state.selectedAttributeId = param;
    },
    setIsDialog(state, param: boolean) {
      state.isDialog = param;
    },
    setAttributePrototypeAddEdit(state, param: AttributePrototypeAddEdit) {
      state.attributePrototypeAddEdit = param;
    },

    setIsMapperDialog(state, param: boolean) {
      state.isMapperDialog = param;
    },
    setMapperItems(state, param: AttributePrototypeMapperResponse) {
      state.mapperCount = param.totalCount;
      state.mapperItems = param.items;
    },
    setMapperOptions(state, param: DataOptions) {
      state.mapperOptions = param;
    },
    setMapperFilters(state, param: Array<CriterionItemRequest> | null) {
      state.mapperFilters = param;
    },
    setMapperSelectedItems(state, param: Array<AttributePrototypeMapper>) {
      state.mapperSelectedItems = param;
    },
    setMapperSuggestions(state, param: Array<MapperSuggestion>) {
      state.mapperSuggestions = param;
    },
    setMapperAddModel(state, param: MapperSuggestion) {
      state.mapperAddModel = param;
    },

    setMapperAttributeSearchFilters(state, param: Array<CriterionItemRequest> | null) {
      state.mapperAttributeSearchFilters = param;
    },
    setMapperAttributeSearchSelectedAttributeId(state, param: number | null) {
      state.mapperAttributeSearchSelectedAttributeId = param;
    },
    setMapperAttributeSearchDataTableOptions(state, param: DataOptions) {
      state.mapperAttributeSearchDataTableOptions = param;
    },
    setMapperAttributeSearchAttributes(state, param: AttributePrototypesResponse) {
      state.mapperAttributeSearchAttributes = param.items;
      state.mapperAttributeSearchAttributesCount = param.totalCount;
    },
  },
  actions: {
    async downloadAttributes(context) {
      const { commit, state } = _moduleActionContext(context);
      const attributes = await getAttributePrototypes({
        count: state.dataTableOptions.itemsPerPage,
        page: state.dataTableOptions.page - 1,
        criterions: _.cloneDeep(state.filters),
        filterName: FILTER_NAMES.AttributePrototypeListFilter,
      });
      commit.setAttributes(attributes);
    },
    async downloadSearchAttributes(context) {
      const { commit, state } = _moduleActionContext(context);
      const attributes = await getAttributePrototypes({
        count: state.mapperAttributeSearchDataTableOptions.itemsPerPage,
        page: state.mapperAttributeSearchDataTableOptions.page - 1,
        criterions: _.cloneDeep(state.mapperAttributeSearchFilters),
        filterName: FILTER_NAMES.AttributePrototypeListFilter,
      });
      commit.setMapperAttributeSearchAttributes(attributes);
    },
    async removeAttribute(context, attributePrototypeId: number) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      if (await rootDispatch.popup.addConfirmRemoveElement()) {
        await removeAttributePrototype(attributePrototypeId);
        await dispatch.downloadAttributes();
      }
    },
    async lockAttribute(context, attributePrototypeId: number) {
      const { dispatch } = _moduleActionContext(context);
      await lockAttributePrototype(attributePrototypeId);
      await dispatch.downloadAttributes();
    },
    async unlockAttribute(context, attributePrototypeId: number) {
      const { dispatch } = _moduleActionContext(context);
      await unlockAttributePrototype(attributePrototypeId);
      await dispatch.downloadAttributes();
    },
    async openNewAttributeDialog(context) {
      const { commit } = _moduleActionContext(context);
      commit.setAttributePrototypeAddEdit(new AttributePrototypeAddEdit());
      commit.setSelectedAttributeId(null);
      commit.setIsDialog(true);
    },
    async openEditAttributeDialog(context, attribute: AttributePrototype) {
      const { commit } = _moduleActionContext(context);
      const attributePrototypeAddEdit = AttributePrototypeAddEdit.fromAttributePrototype(attribute);
      commit.setAttributePrototypeAddEdit(attributePrototypeAddEdit);
      commit.setSelectedAttributeId(attribute.id);
      commit.setIsDialog(true);
    },
    async saveAttribute(context) {
      const { rootCommit, state, rootDispatch, dispatch, commit } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (state.selectedAttributeId === null) {
        await addAttributePrototype(state.attributePrototypeAddEdit);
      } else {
        await editAttributePrototype(state.attributePrototypeAddEdit, state.selectedAttributeId);
      }
      rootDispatch.popup.addOperationSuccessfulSnackbar();
      commit.setIsDialog(false);
      rootCommit.attributes.setAttributePrototypes(null);
      await dispatch.downloadAttributes();
    },
    async openMapperDialog(context, attributePrototypeId: number) {
      const { dispatch, commit } = _moduleActionContext(context);
      commit.setSelectedAttributeId(attributePrototypeId);
      await dispatch.downloadMapperItems();
      commit.setIsMapperDialog(true);
      commit.setMapperAddModel(new MapperSuggestion());
    },

    async downloadMapperItems(context) {
      const { state, commit } = _moduleActionContext(context);
      if (state.selectedAttributeId !== null) {
        const mapperItems = await getAttributePrototypeMappers(state.selectedAttributeId, {
          count: state.mapperOptions.itemsPerPage,
          page: state.mapperOptions.page - 1,
          criterions: _.cloneDeep(state.mapperFilters),
          filterName: FILTER_NAMES.AttributeMapperFilter,
        });
        commit.setMapperItems(mapperItems);
      }
    },
    async downloadMapperSuggestions(context) {
      const { state, commit } = _moduleActionContext(context);
      if (state.selectedAttributeId !== null) {
        const mapperItems = await getMapperSuggestions(state.selectedAttributeId);
        commit.setMapperSuggestions(mapperItems);
      }
    },
    async addMappingFromSuggestions(context) {
      const { state, dispatch } = _moduleActionContext(context);
      const suggestions = state.mapperSuggestions.filter((x) => x.newValue);
      if (suggestions.length !== 0 && state.selectedAttributeId) {
        await addAttributePrototypeMappersFromSuggestions(state.selectedAttributeId, suggestions);
        await dispatch.downloadMapperSuggestions();
      }
    },
    async addMappingItem(context) {
      const { state, rootDispatch, dispatch, commit } = _moduleActionContext(context);
      if (state.selectedAttributeId !== null) {
        await rootDispatch.failIfRequestsInProgress();
        await addAttributePrototypeMapper(state.selectedAttributeId, state.mapperAddModel);
        await rootDispatch.popup.addOperationSuccessfulSnackbar();
        commit.setMapperAddModel(new MapperSuggestion());
        await dispatch.downloadMapperItems();
      }
    },
    async editMappingItem(context, attributePrototypeMapper: AttributePrototypeMapper) {
      const { state, rootDispatch } = _moduleActionContext(context);
      if (state.selectedAttributeId !== null) {
        await rootDispatch.failIfRequestsInProgress();
        await editAttributePrototypeMapper(
          state.selectedAttributeId,
          {
            newValue: attributePrototypeMapper.newValue,
            oldValue: attributePrototypeMapper.oldValue,
          },
          attributePrototypeMapper.id
        );
        await rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
    async removeMappingItem(context, mappingId: number) {
      const { state, rootDispatch, dispatch } = _moduleActionContext(context);
      if (state.selectedAttributeId !== null) {
        if (
          state.mapperItems.find((v) => v.id === mappingId)?.usedInAttributes.length === 0 ||
          (await rootDispatch.popup.addConfirmRemoveElement())
        ) {
          await removeAttributePrototypeMapper(state.selectedAttributeId, mappingId);
          rootDispatch.popup.addOperationSuccessfulSnackbar();
          await dispatch.downloadMapperItems();
        }
      }
    },
    async copyAttributeMappers(context) {
      const { state, rootDispatch, dispatch } = _moduleActionContext(context);
      if (state.mapperAttributeSearchSelectedAttributeId !== null) {
        await rootDispatch.failIfRequestsInProgress();
        await addAttributePrototypeMappers(
          state.mapperAttributeSearchSelectedAttributeId,
          state.mapperSelectedItems.map((x) => x.id)
        );
        rootDispatch.popup.addOperationSuccessfulSnackbar();
        await dispatch.downloadMapperItems();
      } else {
        rootDispatch.popup.addSnackbar(new SnackbarModel(filterCapitalize(i18n.t("selectAnAttributeFirst")), "error"));
        throw new Error("Attribute not selected");
      }
    },
  },
});

export default AdministrationAttributesModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, AdministrationAttributesModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationAttributesModule);
